import { CountryIcon, SystemIcon } from '@monorepo/nova-icons';
import { alpha, Box, Stack, Typography } from '@monorepo/nova-ui';
import { styled } from '@mui/material';

export const StyledLanguageDropDown = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(5.5),
  [theme.breakpoints.up('md')]: {
    marginTop: theme.spacing(8),
    marginBottom: theme.spacing(10),
  },
}));

export const StyledLanguageDropDownSelector = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'isOpen' && prop !== 'disabled' && prop !== 'hideLabel',
})<{
  isOpen: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
}>(({ theme, isOpen, disabled, hideLabel }) => ({
  position: 'relative',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(2),
  width: hideLabel ? 'auto' : theme.spacing(38),
  margin: '0 auto',
  border: `1px solid ${theme.palette.text.fifth}`,
  borderRadius: theme.spacing(4),
  padding: `${theme.spacing(2)} ${theme.spacing(6)} ${theme.spacing(2)} ${theme.spacing(2)}`,
  backgroundColor: 'transparent',
  cursor: disabled ? 'not-allowed' : 'pointer',
  userSelect: 'none',
  WebkitUserSelect: 'none',
  MozUserSelect: 'none',
  msUserSelect: 'none',
  transition: theme.transitions.create(['all'], {
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.up('sm')]: {
    width: hideLabel ? 'auto' : theme.spacing(34),
    padding: theme.spacing(2),
  },
  ...(disabled && {
    opacity: 0.5,
  }),
  '&:hover': {
    borderColor: disabled ? theme.palette.text.fifth : theme.palette.text.third,
  },
  ...(isOpen && {
    borderColor: theme.palette.background.contrast,
    backgroundColor: theme.palette.background.contrast,
  }),
}));

export const StyledLanguageDropdownContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'openDownward' && prop !== 'noBlur',
})<{ openDownward?: boolean; noBlur?: boolean }>(
  ({ theme, openDownward, noBlur }) => ({
    position: 'absolute',
    ...(openDownward
      ? { top: '100%', marginTop: theme.spacing(2) }
      : { bottom: '100%', marginBottom: theme.spacing(2) }),
    left: '50%',
    transform: 'translateX(-50%)',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    width: theme.spacing(38),
    maxHeight: theme.spacing(75),
    border: `1px solid ${theme.palette.text.fifth}`,
    borderRadius: theme.spacing(4),
    backgroundColor: noBlur
      ? theme.palette.background.paper
      : alpha(theme.palette.background.paper, 0.1),
    backdropFilter: noBlur ? 'none' : `blur(${theme.spacing(1.5)})`,
    WebkitBackdropFilter: noBlur ? 'none' : `blur(${theme.spacing(1.5)})`,
    zIndex: 1000,
    overflowY: 'auto',
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(34),
      gap: theme.spacing(1),
      padding: theme.spacing(2),
    },
  }),
);

export const StyledLanguageDropdownOption = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  cursor: 'pointer',
  transition: theme.transitions.create(['background-color'], {
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(1),
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const StyledLanguageCountryIcon = styled(CountryIcon)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(24),
}));

export const StyledLanguageCaretDownIcon = styled(SystemIcon, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{
  isOpen?: boolean;
}>(({ theme, isOpen }) => ({
  fontSize: theme.typography.pxToRem(12),
  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: theme.transitions.create(['transform'], {
    duration: theme.transitions.duration.shorter,
  }),
}));

export const StyledLanguageLabel = styled(Box)(({ theme }) => ({
  display: 'flex',
  flex: 1,
  alignItems: 'center',
  gap: theme.spacing(2),
}));

export const StyledLanguageLabelText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  userSelect: 'none',
  WebkitUserSelect: 'none',
  MozUserSelect: 'none',
  msUserSelect: 'none',
}));
